import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { PostCard } from './PostCard';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { SocialPost } from '../types';
import '@aejkatappaja/phantom-ui';

export interface FilterOption {
  id: string;
  label: string;
  text: string;
}

interface BusinessFeedViewProps {
  apiEndpoint: string;
  activeFilter: string;
  searchQuery?: string;
  emptyTitle: string;
  emptyDescription: string;
}

function BusinessFeedPostPlaceholder() {
  return (
    <Card className="overflow-hidden border-border/60 shadow-sm">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 p-4 pb-3">
        <Avatar className="h-10 w-10" data-shimmer-width={40} data-shimmer-height={40}>
          <AvatarFallback>AB</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1.5 min-w-0">
          <p className="text-sm font-semibold">Placeholder Member Name</p>
          <p className="text-xs text-muted-foreground">2 hours ago</p>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 pt-0">
        <p className="text-sm leading-relaxed">
          Placeholder post content used for skeleton measurement while activity items load.
        </p>
        <div className="h-28 w-full rounded-xl bg-muted/40" data-shimmer-height={112} />
        <div className="flex items-center gap-4 pt-1">
          <span className="text-xs text-muted-foreground">Like</span>
          <span className="text-xs text-muted-foreground">Comment</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function BusinessFeedView({
  apiEndpoint,
  activeFilter,
  searchQuery = '',
  emptyTitle,
  emptyDescription,
}: BusinessFeedViewProps) {
  const { communityId } = useParams<{ communityId: string }>();

  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const loaderRef = useRef<HTMLDivElement>(null);
  const fetchIdRef = useRef(0);

  const fetchItems = useCallback(
    async (currentCursor: string | null, reset = false) => {
      if (!communityId) return;
      if (!reset && isLoading) return;

      const fetchId = ++fetchIdRef.current;
      setIsLoading(true);
      setError(null);

      try {
        const urlDelimiter = apiEndpoint.includes('?') ? '&' : '?';
        const url = `${apiEndpoint}${urlDelimiter}filter=${activeFilter}&limit=10${currentCursor ? `&cursor=${currentCursor}` : ''}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`;
        const response = await axiosInstance.get(url);

        if (fetchId !== fetchIdRef.current) return;

        const data = response.data;

        if (reset) {
          setPosts(data.posts);
        } else if (currentCursor) {
          setPosts((prev) => [...prev, ...data.posts]);
        } else {
          setPosts(data.posts);
        }

        setCursor(data.nextCursor);
        setHasMore(!!data.nextCursor);
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        console.error('Failed to fetch items:', err);
        setError('Failed to load items. Please try again.');
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [communityId, activeFilter, searchQuery, isLoading, apiEndpoint]
  );

  useEffect(() => {
    setPosts([]);
    setCursor(null);
    setHasMore(true);
    fetchItems(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, communityId, searchQuery, apiEndpoint]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && posts.length > 0) {
          fetchItems(cursor);
        }
      },
      { threshold: 0.1 }
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [cursor, hasMore, isLoading, fetchItems, posts.length]);

  const handlePostDelete = (postId: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const isInitialLoading = isLoading && posts.length === 0 && !error;

  return (
    <div className="space-y-4 max-w-2xl">
      {error && posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed rounded-xl border-border/50 bg-accent/5 text-center">
          <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => fetchItems(null, true)}>Retry</Button>
        </div>
      ) : isInitialLoading ? (
        <phantom-ui
          loading
          animation="shimmer"
          reveal={0.2}
          count={3}
          count-gap={24}
          class="block space-y-6"
        >
          <BusinessFeedPostPlaceholder />
        </phantom-ui>
      ) : (
        <div className="space-y-6">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onDelete={handlePostDelete}
              isLeadDashboard={true}
              communityId={communityId}
            />
          ))}

          {isLoading && posts.length > 0 && (
            <phantom-ui loading animation="shimmer" reveal={0.2} class="block">
              <BusinessFeedPostPlaceholder />
            </phantom-ui>
          )}

          {!hasMore && posts.length > 0 && (
            <div className="text-center p-8 text-muted-foreground text-sm">
              You've reached the end of the list.
            </div>
          )}

          {!hasMore && posts.length === 0 && !isLoading && !error && (
            <div className="text-center p-12 border border-dashed rounded-xl border-border/50 bg-accent/5 text-muted-foreground">
              <p className="text-lg font-medium">{emptyTitle}</p>
              <p className="text-sm">{emptyDescription}</p>
            </div>
          )}

          <div ref={loaderRef} className="h-4 w-full" />
        </div>
      )}
    </div>
  );
}
